import { type FC, useEffect, useRef, useSyncExternalStore } from 'react';

import {
  DetailsSidebar,
  DetailsSidebarProvider,
  useDetailsSidebar,
} from '@eluve/components';

import { MiniAppPanels } from './MiniAppPanels';
import {
  getTasksPanelSnapshot,
  subscribeTasksPanel,
  toggleTasksPanel,
} from './apps/clinical-tasks/tasks-panel-store';
import type { MiniAppContext, MiniAppDefinition } from './types';

/**
 * Appointment-summary side rail: `MiniAppPanels` hosted in a `DetailsSidebar`
 * under its own nested provider (shadows the page provider only inside this
 * subtree, where nothing consumes it). The host supplies the grid the fixed
 * variant self-places into (col 2, rows 2-3).
 *
 * Invariants:
 *  - `MiniAppPanels` stays mounted even when showing nothing — the tasks
 *    panel hook is the poller feeding the Tasks button. The `:has()` rule
 *    hides the rail chrome instead (display:contents keeps the aside a grid
 *    item).
 *  - The Tasks button toggles the tasks-panel store, not this context (it
 *    lives in a different tree branch). `TasksSheetBridge` maps store toggles
 *    onto the Sheet in overlay mode; in fixed mode the panel obeys
 *    `collapsed` itself, emptying the aside.
 *
 * Follow-ups: swap the direct clinical-tasks store import for a
 * registry-level visibility channel (keeps the rail app-agnostic); a
 * `hideWhenEmpty` option and breakpoint variant belong on DetailsSidebar.
 */
export const MiniAppSummarySideRail: FC<{
  apps: readonly MiniAppDefinition[];
  context: MiniAppContext;
}> = ({ apps, context }) => {
  return (
    <DetailsSidebarProvider
      onOpenChange={(open) => {
        // Sheet dismissed via X/overlay/Escape → reflect it on the Tasks
        // button (store), so the button's next click reopens rather than
        // "closing" an already-closed sheet.
        if (!open && !getTasksPanelSnapshot().collapsed) toggleTasksPanel();
      }}
    >
      <TasksSheetBridge />
      {/* display:contents keeps the DetailsSidebar aside a direct grid item
          of the host grid; the :has() rule hides the rail chrome (border/
          width) whenever every panel rendered null — the padding div is
          always present, so emptiness is checked one level in. */}
      <div className="contents [&>aside:has(>div:empty)]:hidden">
        <DetailsSidebar title="Tasks" fixedWidth="md">
          {/* The aside itself is unpadded (it was the old hand-rolled rail
              that carried p-4) — pad here so the panels' rounded cards keep
              their breathing room against the rail border. */}
          <div className="p-4">
            <MiniAppPanels
              slot="appointment-summary-side"
              apps={apps}
              context={context}
            />
          </div>
        </DetailsSidebar>
      </div>
    </DetailsSidebarProvider>
  );
};

/**
 * Maps Tasks-button toggles (tasks-panel store) onto the sidebar Sheet in
 * overlay mode. Skips the initial snapshot so the Sheet doesn't auto-open on
 * page load — only an explicit toggle opens it.
 */
const TasksSheetBridge: FC = () => {
  const { variant, setOpen } = useDetailsSidebar();
  const snapshot = useSyncExternalStore(
    subscribeTasksPanel,
    getTasksPanelSnapshot,
  );
  const isFirstRun = useRef(true);
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    if (variant === 'overlay') setOpen(!snapshot.collapsed);
  }, [variant, snapshot.collapsed, setOpen]);
  return null;
};
